/**
 * wayai dimensions — an organization's access dimensions and their values (#5358).
 *
 * The same `/api/setup/organizations/:id/access-dimensions` routes the app uses, on an org-admin
 * grant. Which values each person, hub or agent is assigned is not set here. A refusal's message is
 * the route's, sanitized.
 */

import { parseArgs } from 'node:util';
import { getAccessToken, requireAuth } from '../lib/auth.js';
import { ApiClient, ApiError } from '../lib/api-client.js';
import { extractApiMessage } from '../lib/errors.js';
import { expected } from '../lib/expected.js';
import { readRepoConfigUnlessBlocked } from '../lib/repo-config.js';
import { sanitizeTerminalText } from '../lib/terminal-output.js';
import { UUID_RE, wantsHelp } from '../lib/utils.js';
import {
  ACCESS_ID_MAX_LENGTH,
  MAX_ACCESS_DIMENSIONS_PER_ORG,
  MAX_ACCESS_VALUES_PER_DIMENSION,
} from '@wayai/core/contracts';

const USAGE = `
wayai dimensions — an organization's access dimensions and their values

Usage:
  wayai dimensions list [--org <org_id>] [--json]
  wayai dimensions create <dimension> --label <label> [--org <org_id>] [--json]
  wayai dimensions relabel <dimension> --label <label> [--org <org_id>] [--json]
  wayai dimensions add-value <dimension> <value> --label <label> [--org <org_id>] [--json]
  wayai dimensions relabel-value <dimension> <value> --label <label> [--org <org_id>] [--json]
  wayai dimensions retire-value <dimension> <value> [--org <org_id>] [--json]
  wayai dimensions restore-value <dimension> <value> [--org <org_id>] [--json]

A record type in one of the organization's bases names a dimension in its x-access
declaration (for example "class"). Where that declaration is in enforce mode, a call
reaches only the records whose value (for example "7A") is among the caller's; in report
mode it only counts what enforcing would hide. A dimension's and a value's id is
fixed once created: change what people read with relabel. A value is never deleted:
retire-value stops it from being assigned again, and records holding it still match.
An organization holds at most ${MAX_ACCESS_DIMENSIONS_PER_ORG} dimensions of at most ${MAX_ACCESS_VALUES_PER_DIMENSION} values each, retired ones
counted. Ids are ASCII letters, digits, "_" and "-", up to ${ACCESS_ID_MAX_LENGTH} characters.

The organization is --org, else this repo's (wayai-ws/wayai.yaml). Changes need an
organization admin.
`.trim();

const SUBCOMMANDS = ['list', 'create', 'relabel', 'add-value', 'relabel-value', 'retire-value', 'restore-value'] as const;
type Subcommand = (typeof SUBCOMMANDS)[number];

/** How many positionals each subcommand takes after its name, and whether it needs --label. */
const SHAPE: Record<Subcommand, { ids: number; label: boolean }> = {
  list: { ids: 0, label: false },
  create: { ids: 1, label: true },
  relabel: { ids: 1, label: true },
  'add-value': { ids: 2, label: true },
  'relabel-value': { ids: 2, label: true },
  'retire-value': { ids: 2, label: false },
  'restore-value': { ids: 2, label: false },
};

function usageError(message: string): Error {
  return expected(`${message}\n\n${USAGE}`);
}

function parse(args: string[]) {
  try {
    return parseArgs({
      args,
      options: {
        org: { type: 'string' },
        label: { type: 'string' },
        json: { type: 'boolean' },
      },
      allowPositionals: true,
      strict: true,
    });
  } catch (err) {
    throw usageError(err instanceof Error ? err.message : String(err));
  }
}

/** The org from `--org`, else the repo's binding; refused when neither names one. */
function resolveOrgId(flag: string | undefined): string {
  if (flag !== undefined) {
    if (!UUID_RE.test(flag)) throw expected(`Invalid --org ${JSON.stringify(flag)}: expected an organization id (a UUID).`);
    return flag;
  }
  const bound = readRepoConfigUnlessBlocked()?.organization_id;
  if (bound) return bound;
  throw expected('No organization: pass --org <org_id>, or run this inside a workspace scoped with `wayai init`.');
}

function failure(err: unknown): unknown {
  if (err instanceof ApiError && err.isExpected) return expected(sanitizeTerminalText(extractApiMessage(err)));
  return err;
}

export async function dimensionsCommand(args: string[]): Promise<void> {
  if (wantsHelp(args) || args.length === 0) {
    console.log(USAGE);
    return;
  }
  const { values, positionals } = parse(args);
  const [sub, ...ids] = positionals;
  if (!SUBCOMMANDS.includes(sub as Subcommand)) throw usageError(`Unknown subcommand ${JSON.stringify(sub)}.`);
  const shape = SHAPE[sub as Subcommand];
  if (ids.length !== shape.ids) {
    throw usageError(`${sub} takes ${shape.ids === 0 ? 'no arguments' : shape.ids === 1 ? 'a <dimension>' : 'a <dimension> and a <value>'}.`);
  }
  if (shape.label && values.label === undefined) throw usageError(`${sub} needs --label <label>.`);
  if (!shape.label && values.label !== undefined) throw usageError(`--label does not apply to ${sub}.`);
  const orgId = resolveOrgId(values.org);
  const [dimensionId, valueId] = ids;
  const label = values.label!;

  const { config, accessToken } = await requireAuth();
  // A browser login is a JWT that expires; refresh it on a 401 instead of failing.
  const api = new ApiClient({ apiUrl: config.api_url, accessToken, onUnauthorized: () => getAccessToken(config) });

  let data: unknown;
  let done = '';
  try {
    switch (sub as Subcommand) {
      case 'list': {
        const { data: dimensions } = await api.listAccessDimensions(orgId);
        if (values.json) {
          console.log(JSON.stringify(dimensions, null, 2));
          return;
        }
        if (dimensions.length === 0) {
          console.log(`Organization ${orgId} defines no access dimensions.`);
          return;
        }
        for (const dimension of dimensions) {
          console.log(`${sanitizeTerminalText(dimension.dimension_id)}  ${sanitizeTerminalText(dimension.label)}`);
          for (const value of dimension.values) {
            const retired = value.retired_at ? '  (retired)' : '';
            console.log(`  ${sanitizeTerminalText(value.value_id)}  ${sanitizeTerminalText(value.label)}${retired}`);
          }
        }
        return;
      }
      case 'create':
        ({ data } = await api.createAccessDimension(orgId, { dimension_id: dimensionId, label }));
        done = `Created dimension ${dimensionId}.`;
        break;
      case 'relabel':
        ({ data } = await api.relabelAccessDimension(orgId, dimensionId, label));
        done = `Relabelled dimension ${dimensionId}.`;
        break;
      case 'add-value':
        ({ data } = await api.createAccessValue(orgId, dimensionId, { value_id: valueId, label }));
        done = `Added value ${valueId} to dimension ${dimensionId}.`;
        break;
      case 'relabel-value':
        ({ data } = await api.updateAccessValue(orgId, dimensionId, valueId, { label }));
        done = `Relabelled value ${valueId} of dimension ${dimensionId}.`;
        break;
      case 'retire-value':
        ({ data } = await api.updateAccessValue(orgId, dimensionId, valueId, { retired: true }));
        done = `Retired value ${valueId} of dimension ${dimensionId}: it can no longer be assigned, and records holding it still match.`;
        break;
      case 'restore-value':
        ({ data } = await api.updateAccessValue(orgId, dimensionId, valueId, { retired: false }));
        done = `Restored value ${valueId} of dimension ${dimensionId}.`;
        break;
    }
  } catch (err) {
    throw failure(err);
  }
  console.log(values.json ? JSON.stringify(data, null, 2) : sanitizeTerminalText(done));
}
